import { TutorSectionTabs } from '@/components/tutor/TutorSectionTabs';
import { useId, useRef, useState } from 'react';
import { DEFAULT_TUTOR_NAME, TutorAvatar } from '@/components/tutor/TutorAvatar';

const FUNCTIONS = [
  {
    name: 'Start with a source',
    child: 'Reads the selected text and checks what the task asks.',
    tutor: 'Clarifies the task or an unfamiliar word before offering analysis.',
    limit:
      'An uploaded source is not proof that AI help is permitted. The separate English trial requires reviewed, published original materials.',
  },
  {
    name: 'Make their own attempt',
    child: 'Chooses an idea, evidence and an explanation, or identifies where they are stuck.',
    tutor: 'Uses that attempt to decide what help would be useful.',
    limit:
      'A saved attempt records participation. It does not establish the quality of the thinking.',
  },
  {
    name: 'Get the right amount of help',
    child: 'Makes choices, tries the suggested method and returns to the task.',
    tutor:
      'May clarify, hint, explain or model a method. Help should reduce when the child can continue.',
    limit:
      'Tutor help exists in Study. The English trial uses the learner’s attempts and requested help; its effectiveness is not established.',
  },
  {
    name: 'Connect ideas and evidence',
    child: 'Builds and checks the link between an idea, a detail and their explanation.',
    tutor: 'Can help locate a missing link without choosing the child’s final judgment.',
    limit:
      'The English trial Answer Map supports the child’s reasoning. A complete map is not learning proof.',
  },
  {
    name: 'Check against the source',
    child: 'Checks quotations, context and whether the evidence supports the claim.',
    tutor: 'Prompts a source check and identifies uncertainty.',
    limit:
      'AI can misread or invent details. An exact quotation check does not establish whether that evidence supports the interpretation.',
  },
  {
    name: 'Try a new task, then return later',
    child: 'Tries a reviewed new text with help recorded, and returns for a later check.',
    tutor: 'Keeps access accommodations and records any analysis help used.',
    limit:
      'Study has practice and reviews. The separate English trial uses different texts and records support use. A retry of the same answer is not transfer.',
  },
  {
    name: 'Give adults a useful summary',
    child: 'Keeps ownership of their private working and explanation.',
    tutor: 'Makes recorded activity, support and next steps understandable to authorised adults.',
    limit:
      'English trial process records show attempts, help and checks when available. The summary below is fictional, not your child’s evidence.',
  },
] as const;

const BRANCHES = {
  novice: {
    label: 'Needs the method',
    steps: [
      ['Your child', '“I do not know how to find evidence for a feeling.”'],
      [
        'Tutor',
        'Explains that actions can suggest feelings. On a different text, Noah displays a certificate facing the door. This could suggest he wants recognition.',
      ],
      [
        'Why',
        'The Tutor makes the method explicit using another text. The child still has to choose evidence from Asha’s passage.',
      ],
      [
        'Your child',
        'Returns to Asha’s passage, chooses the repeated smoothing and explains why that action could suggest unease.',
      ],
      [
        'Tutor',
        'Asks the child to check their explanation against the source. It does not write the child’s final response.',
      ],
      [
        'Why',
        'The child chooses the detail and supplies the explanation. The example alone does not show independent ability.',
      ],
    ],
    support: 'A method explanation and a worked example on another text.',
  },
  stuck: {
    label: 'Gets stuck',
    steps: [
      ['Your child', '“She is nervous because someone calls her name.”'],
      [
        'Tutor',
        '“Being called could also make someone excited. Would you check her actions or her words?”',
      ],
      [
        'Why',
        'The hint responds to this attempt. The child chooses which part of the passage to inspect.',
      ],
      ['Your child', 'Chooses actions, but still cannot explain what they suggest.'],
      [
        'Tutor',
        'Models another text: Noah displays a certificate facing the door. Visitors can see it, so he may want recognition.',
      ],
      [
        'Why',
        'When a hint is not enough, the Tutor teaches a method. It leaves the current passage for the child to interpret.',
      ],
      [
        'Your child',
        '“She smooths the corner twice. Repeating this small movement could suggest she is unsettled.”',
      ],
      ['Tutor', 'Asks the child to check that detail and explain the link in their own words.'],
      [
        'Why',
        'The child changes both the evidence and the explanation. Help remains part of this record.',
      ],
    ],
    support: 'One comparison hint and a worked example on another text.',
  },
  competent: {
    label: 'Ready for less help',
    steps: [
      [
        'Your child',
        'Chooses the repeated smoothing and explains that a small repeated movement may suggest nervousness.',
      ],
      ['Tutor', '“Which words support your idea? Could that action have another explanation?”'],
      [
        'Why',
        'The Tutor checks the reasoning without giving a model answer when the child can already continue.',
      ],
      [
        'Your child',
        'Checks the source and revises the claim to say the action “could suggest” nervousness.',
      ],
      ['Tutor', 'Suggests a reviewed new text for a later check with less help.'],
      [
        'Why',
        'Support reduces in response to the child’s work. Independent performance still needs separate evidence.',
      ],
    ],
    support: 'A source-check question; no worked example.',
  },
} as const;

const FAQS = [
  {
    question: 'Is getting help a problem?',
    answer:
      'Help is a valid part of learning when the task permits it. We record support rather than treat supported work as independent mastery.',
  },
  {
    question: 'Can my child use this for schoolwork?',
    answer:
      'The school and the specific task set the rules. Parent settings do not grant permission. If rules are unclear, check or use separate practice.',
  },
  {
    question: 'What if the AI is wrong?',
    answer:
      'Check the original source. AI feedback can be mistaken or uncertain. Ask for human review when an interpretation or result matters.',
  },
  {
    question: 'What can I see?',
    answer:
      'Your family report shows authorised learning evidence. It does not reveal private answers, working or full Tutor conversations. School reports follow their own sharing permissions.',
  },
  {
    question: 'Does this prove my child has improved?',
    answer:
      'Counts, a polished answer and a completed example do not prove improvement. Reviewed new-task and later checks provide different evidence, with any help recorded.',
  },
] as const;

export function StudyTutorParentGuide({
  initiallyExpanded = false,
  standalone = false,
}: { initiallyExpanded?: boolean; standalone?: boolean } = {}) {
  const [expanded, setExpanded] = useState(initiallyExpanded);
  const [category, setCategory] = useState<'method' | 'example' | 'questions'>('method');
  const panelId = useId();
  const [branch, setBranch] = useState<keyof typeof BRANCHES>('stuck');
  const disclosureId = useId();
  const headingId = useId();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const selected = BRANCHES[branch];

  function closeGuide() {
    setExpanded(false);
    toggleRef.current?.focus();
  }

  return (
    <section className="card-base mt-5" aria-labelledby={headingId}>
      <div className="flex items-start gap-3 sm:gap-4">
        <TutorAvatar size="medium" expression="friendly" animated={false} />
        <div className="min-w-0">
          <p className="eyebrow eyebrow-sky">Learning with Tutor</p>
          <h2 id={headingId} className="mt-2 text-[22px] font-bold text-ink">
            How the Tutor helps your child learn
          </h2>
          <p className="mt-1 text-sm font-bold text-ink-soft">{DEFAULT_TUTOR_NAME} · AI Tutor</p>
        </div>
      </div>
      <p className="mt-2 max-w-3xl text-[15px] leading-relaxed text-ink-soft">
        Our aim is for your child to choose evidence, explain an idea and try again. Tutor help
        supports that thinking. Finishing with help does not establish independent ability.
      </p>
      <p className="mt-3 text-[13px] font-semibold text-ink-soft">
        English example: Scripted illustration · Not a live learning feature · Trial requires
        educator review and publication
      </p>
      {!standalone && (
        <button
          ref={toggleRef}
          type="button"
          aria-expanded={expanded}
          aria-controls={disclosureId}
          className="btn-pill-secondary mt-4 min-h-12 w-full sm:w-auto"
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? 'Close Tutor guide' : 'Read the method and English example'}
        </button>
      )}

      <div id={disclosureId} hidden={!expanded}>
        {expanded && (
          <div className="mt-4 space-y-4">
            <TutorSectionTabs
              label="Tutor guide categories"
              items={[
                { id: 'method', label: 'Learning method' },
                { id: 'example', label: 'Dialogue example' },
                { id: 'questions', label: 'Common questions' },
              ]}
              value={category}
              onChange={setCategory}
              panelId={panelId}
            />
            <div
              id={panelId}
              role="tabpanel"
              aria-labelledby={`${panelId}-tab-${category}`}
              tabIndex={0}
            >
              {category === 'method' && (
                <section aria-label="Tutor functions and evidence limits">
                  <h3 className="text-[20px] font-bold text-ink">Who does the thinking?</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink-soft">
                    Your child owns the judgment, evidence and explanation. These functions explain
                    the method; they do not approve schoolwork use or promise learning gains.
                  </p>
                  <ol className="mt-4 grid gap-3 sm:grid-cols-2">
                    {[
                      'Your child tries first',
                      'Tutor teaches the difficulty',
                      'Your child retries',
                      'A new text checks independent application',
                    ].map((step, i) => (
                      <li
                        key={step}
                        className="rounded-2xl bg-wash-sky p-3 text-sm font-bold text-ink"
                      >
                        {i + 1}. {step}
                      </li>
                    ))}
                  </ol>
                  <details className="mt-4">
                    <summary className="cursor-pointer text-sm font-bold text-ink">
                      See all seven functions and their limits
                    </summary>
                    <div className="mt-4 grid gap-3 md:grid-cols-2">
                      {FUNCTIONS.map((item, index) => (
                        <article
                          key={item.name}
                          className="min-w-0 rounded-[20px] border border-hairline p-4"
                        >
                          <h4 className="text-[16px] font-bold text-ink">
                            {index + 1}. {item.name}
                          </h4>
                          <dl className="mt-3 space-y-2 text-sm leading-relaxed text-ink-soft">
                            <div>
                              <dt className="font-bold text-ink">Your child</dt>
                              <dd>{item.child}</dd>
                            </div>
                            <div>
                              <dt className="font-bold text-ink">Tutor</dt>
                              <dd>{item.tutor}</dd>
                            </div>
                            <div>
                              <dt className="font-bold text-ink">Evidence and limits</dt>
                              <dd>{item.limit}</dd>
                            </div>
                          </dl>
                        </article>
                      ))}
                    </div>
                  </details>
                </section>
              )}

              {category === 'example' && (
                <>
                  <section
                    className="rounded-[24px] bg-wash-sky p-4 sm:p-5"
                    aria-label="Fictional English Tutor example"
                  >
                    <p className="text-xs font-bold uppercase tracking-wide text-ink-soft">
                      Scripted demonstration · English design example
                    </p>
                    <h3 className="mt-2 text-[20px] font-bold text-ink">
                      One passage, different help
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-ink-soft">
                      Asha is a character in this original passage. The learner and Tutor replies
                      are fictional, not your child’s records or a live AI demonstration.
                    </p>
                    <blockquote className="mt-4 rounded-[20px] bg-canvas-pure p-4 text-[15px] leading-relaxed text-ink">
                      When her name was called, Asha stood. She smoothed the same corner of her
                      notes twice. “I’m ready,” she said, but she did not lift her eyes from the
                      page.
                    </blockquote>
                    <p className="mt-3 text-sm font-bold text-ink">
                      Task: How does the writer suggest that Asha is nervous?
                    </p>
                    <p className="mt-3 text-sm text-ink-soft">
                      Choose a fictional learner situation to see how the help changes.
                    </p>
                    <div
                      className="mt-3 flex flex-wrap gap-2"
                      role="group"
                      aria-label="Fictional learner situations"
                    >
                      {(Object.keys(BRANCHES) as Array<keyof typeof BRANCHES>).map((key) => (
                        <button
                          key={key}
                          type="button"
                          aria-pressed={branch === key}
                          className={`min-h-12 rounded-full border px-4 py-2 text-sm font-bold ${branch === key ? 'border-ink bg-ink text-canvas' : 'border-hairline bg-canvas-pure text-ink'}`}
                          onClick={() => setBranch(key)}
                        >
                          {BRANCHES[key].label}
                        </button>
                      ))}
                    </div>
                    <ol
                      className="mx-auto mt-5 max-h-[440px] max-w-[840px] space-y-4 overflow-y-auto overscroll-contain pr-2"
                      tabIndex={0}
                      aria-label={`${selected.label} walkthrough`}
                    >
                      {selected.steps.map(([role, text], index) => (
                        <li
                          key={`${branch}-${index}`}
                          data-speaker={
                            role === 'Tutor'
                              ? 'tutor'
                              : role === 'Your child'
                                ? 'child'
                                : 'explanation'
                          }
                          className={`flex ${role === 'Your child' ? 'justify-end' : role === 'Tutor' ? 'justify-start' : 'justify-center'}`}
                        >
                          {role === 'Tutor' ? (
                            <div className="flex max-w-[94%] items-start gap-2 sm:max-w-[80%] sm:gap-3">
                              <TutorAvatar expression="explaining" animated={false} />
                              <div
                                data-testid="tutor-demo-bubble"
                                className="min-w-0 break-words rounded-[20px] rounded-tl-[6px] border border-hairline bg-canvas-pure px-4 py-3 text-sm leading-relaxed text-ink-soft"
                              >
                                <p className="font-bold text-ink">{DEFAULT_TUTOR_NAME} · Tutor</p>
                                <p className="mt-1">{text}</p>
                              </div>
                            </div>
                          ) : role === 'Your child' ? (
                            <div
                              data-testid="tutor-demo-bubble"
                              className="min-w-0 max-w-[78%] break-words rounded-[20px] rounded-tr-[6px] bg-wash-mint px-4 py-3 text-sm leading-relaxed text-ink-soft sm:max-w-[72%]"
                            >
                              <p className="font-bold text-ink">{role}</p>
                              <p className="mt-1">{text}</p>
                            </div>
                          ) : (
                            <div
                              data-testid="tutor-demo-explanation"
                              className="max-w-[90%] border-l-2 border-brand-sky/30 pl-3 text-xs leading-relaxed text-ink-soft sm:max-w-[68%]"
                            >
                              <p className="font-bold uppercase tracking-wide text-ink">{role}</p>
                              <p className="mt-1">{text}</p>
                            </div>
                          )}
                        </li>
                      ))}
                    </ol>
                    <p className="mt-4 text-sm leading-relaxed text-ink-soft">
                      A hint points the child back to the task. A worked example teaches the method
                      on another text. Help fades when the child can continue, rather than after a
                      fixed number of days.
                    </p>
                  </section>

                  <section
                    className="rounded-[24px] border border-hairline bg-wash-mint p-4 sm:p-5"
                    aria-label="Fictional adult summary"
                  >
                    <p className="text-xs font-bold uppercase tracking-wide text-ink-soft">
                      Sample adult summary · Not your child’s record
                    </p>
                    <h3 className="mt-2 text-[20px] font-bold text-ink">
                      What an adult could learn from this process
                    </h3>
                    <details className="mt-3">
                      <summary className="cursor-pointer text-sm font-bold text-ink">
                        View sample process summary
                      </summary>
                      <dl className="mt-3 space-y-2 text-sm leading-relaxed text-ink-soft">
                        <div>
                          <dt className="font-bold text-ink">Goal</dt>
                          <dd>Use a character’s actions to support an interpretation.</dd>
                        </div>
                        <div>
                          <dt className="font-bold text-ink">Learner work</dt>
                          <dd>
                            Initial attempt recorded. A detail was selected and an explanation
                            revised. Revision quality awaits review.
                          </dd>
                        </div>
                        <div>
                          <dt className="font-bold text-ink">Support used</dt>
                          <dd>{selected.support}</dd>
                        </div>
                        <div>
                          <dt className="font-bold text-ink">Independent check</dt>
                          <dd>Not yet attempted. Independent ability is not established.</dd>
                        </div>
                        <div>
                          <dt className="font-bold text-ink">Next step</dt>
                          <dd>
                            Try a reviewed new text, record any help, and return for a later check.
                          </dd>
                        </div>
                      </dl>
                      <p className="mt-3 text-sm text-ink-soft">
                        This fictional summary omits private answer text. Actual English trial
                        records, when available, show recorded steps and distinguish support use
                        from answer quality.
                      </p>
                    </details>
                  </section>
                </>
              )}

              {category === 'questions' && (
                <section aria-label="Questions parents ask about Tutor">
                  <h3 className="text-[20px] font-bold text-ink">Questions you might have</h3>
                  <div className="mt-3 space-y-2">
                    {FAQS.map((item) => (
                      <details
                        key={item.question}
                        className="rounded-[20px] border border-hairline p-4"
                      >
                        <summary className="min-h-11 cursor-pointer font-bold text-ink">
                          {item.question}
                        </summary>
                        <p className="mt-2 text-sm leading-relaxed text-ink-soft">{item.answer}</p>
                      </details>
                    ))}
                  </div>
                  <p className="mt-4 text-[15px] leading-relaxed text-ink-soft">
                    Ask your child: “Which detail supports your idea, and why?” Give them time to
                    explain. Avoid choosing the evidence or writing the response for them.
                  </p>
                  <p className="mt-2 text-sm text-ink-soft">
                    You can discuss what a helped revision shows, and what still needs checking on a
                    new task. There is no quiz required to use this guide.
                  </p>
                </section>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-hairline pt-4">
              <p className="text-xs text-ink-soft">
                Guide v0.6 · 7 October 2026 · English design under exploration
              </p>
              {!standalone && (
                <button
                  type="button"
                  className="btn-pill-secondary min-h-12 w-full sm:w-auto"
                  onClick={closeGuide}
                >
                  Close guide and return to report
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
